import { useState, useEffect } from 'react';
import { graphqlClient } from '@/services/core/graphqlClient';
import { useAuth } from '@/contexts/AuthContext';

export interface ProgramTypeStatus {
  program_status: string;
  program_status_id: number;
  program_status_mapper_id: string;
  program_type: string;
  program_type_id?: number;
}

export interface UseProgramTypeStatusesResult {
  programTypeStatuses: ProgramTypeStatus[];
  loading: boolean;
  error: string | null;
}

const GET_WORKFLOW_PROGRAM_TYPE_IDS = `
  query GetWorkflowProgramTypeIds($workflowId: Int!) {
    pep_workflow_program_type_mapper(
      where: {workflow_id: {_eq: $workflowId}, is_deleted: {_eq: false}}
    ) {
      program_type_id
    }
  }
`;

const GET_PROGRAM_TYPE_STATUSES_BY_IDS = `
  query GetProgramTypeStatusesByIds($programTypeIds: [Int!]!) {
    pep_vw_program_type_status(
      where: {program_type_id: {_in: $programTypeIds}}
    ) {
      program_status
      program_status_id
      program_type
      program_status_mapper_id
      program_type_id
    }
  }
`;

const GET_ALL_PROGRAM_TYPE_STATUSES = `
  query GetAllProgramTypeStatuses {
    pep_vw_program_type_status {
      program_status
      program_status_id
      program_type
      program_status_mapper_id
      program_type_id
    }
  }
`;

export const useProgramTypeStatuses = (workflowId?: number, isFromGlobalSearch?: boolean): UseProgramTypeStatusesResult => {
  const { hasuraToken } = useAuth();
  const [statuses, setStatuses] = useState<ProgramTypeStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatuses = async () => {
    if (!hasuraToken) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    // If from global search, fetch all statuses without filter
    if (isFromGlobalSearch) {
      try {
        setLoading(true);
        const statusResponse = await graphqlClient.query(
          GET_ALL_PROGRAM_TYPE_STATUSES,
          {},
          hasuraToken
        );

        if (statusResponse.success && statusResponse.data) {
          setStatuses(statusResponse.data.pep_vw_program_type_status || []);
          setError(null);
        } else {
          setError(statusResponse.errors?.[0] || 'Failed to fetch program type statuses');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch program type statuses');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!workflowId) {
      setStatuses([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Step 1: Fetch program_type_ids for the workflow
      const programTypeResponse = await graphqlClient.query(
        GET_WORKFLOW_PROGRAM_TYPE_IDS,
        { workflowId },
        hasuraToken
      );

      if (!programTypeResponse.success || !programTypeResponse.data) {
        setError(programTypeResponse.errors?.[0] || 'Failed to fetch workflow program types');
        setStatuses([]);
        setLoading(false);
        return;
      }

      const programTypeIds = programTypeResponse.data.pep_workflow_program_type_mapper?.map(
        (item: any) => item.program_type_id
      ) || [];

      // If no program type IDs, return empty array
      if (programTypeIds.length === 0) {
        setStatuses([]);
        setError(null);
        setLoading(false);
        return;
      }

      // Step 2: Fetch program type statuses filtered by program_type_ids
      const statusResponse = await graphqlClient.query(
        GET_PROGRAM_TYPE_STATUSES_BY_IDS,
        { programTypeIds },
        hasuraToken
      );

      if (statusResponse.success && statusResponse.data) {
        setStatuses(statusResponse.data.pep_vw_program_type_status || []);
        setError(null);
      } else {
        setError(statusResponse.errors?.[0] || 'Failed to fetch program type statuses');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch program type statuses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, [hasuraToken, workflowId, isFromGlobalSearch]);

  return {
    programTypeStatuses: statuses,
    loading,
    error
  };
};
